import { useDialog, useRenderNotifier } from '@wui/common-library'
import { BriefStatusResponse, ConferenceType, useIM } from '@wui/im'

import { isCurrentUserRoomMember } from '@/entities/room'

import { useLocalization } from '@/shared/composables/localization'
import { Room } from '@/shared/types/store'
import { toPServedUser } from '@/shared/utils/uri'

import JoinToChatAwaitDialog from '../ui/join-to-chat-await-dialog.vue'
import JoinToChatDialog from '../ui/join-to-chat-dialog.vue'

export type EnsureConferenceRoomAccessIntent = 'chat' | 'prepare-to-call'

export type EnsureConferenceRoomAccessOptions = {
  resolveRoom?: (roomId: string) => Room | undefined
  room?: Room
  intent?: EnsureConferenceRoomAccessIntent
  skipChatNavigation?: boolean
}

const resolveRoomFromOptions = (roomId: string, options: EnsureConferenceRoomAccessOptions): Room | undefined => {
  if (options.room?.id === roomId) {
    return options.room
  }

  return options.resolveRoom?.(roomId)
}

const applyBriefStatus = (room: Room | undefined, roomInfo: BriefStatusResponse) => {
  if (!room) {
    return
  }

  room.name = roomInfo.name || room.name
  room.mode = roomInfo.mode
  room.isEnabled = roomInfo.isEnabled
}

export const ensureConferenceRoomAccess = async (
  roomId: string,
  options: EnsureConferenceRoomAccessOptions = {},
): Promise<boolean> => {
  const pServed = toPServedUser(roomId, true)
  const intent = options.intent ?? 'chat'
  const { requestChatHistory, requestBriefStatus, subscribeOnConferenceUpdates } = useIM()
  const { showDialog } = useDialog()
  const { notifyError } = useRenderNotifier()
  const { t } = useLocalization()

  const room = resolveRoomFromOptions(roomId, options)
  if (isCurrentUserRoomMember(room)) {
    return true
  }

  await subscribeOnConferenceUpdates({ pServed })

  try {
    await requestChatHistory({ messagesLimit: 1, pServed })
    const roomAfterHistory = resolveRoomFromOptions(roomId, options)
    if (isCurrentUserRoomMember(roomAfterHistory)) {
      return true
    }
  } catch {
    // not a member yet
  }

  const roomInfo = await requestBriefStatus({ contactId: pServed })
  if (!roomInfo) {
    return false
  }

  applyBriefStatus(resolveRoomFromOptions(roomId, options), roomInfo)

  if (!roomInfo.isEnabled) {
    notifyError(t('common.joinToChatDisabledByOwner'))
    return false
  }

  const dialogProps = {
    pServed,
    roomInfo,
    hasOverlay: true as const,
    ...(options.skipChatNavigation !== undefined && { skipChatNavigation: options.skipChatNavigation }),
  }

  if (roomInfo.mode === ConferenceType.MODERATED) {
    if (intent === 'prepare-to-call') {
      // Запрос модератору уходит с экрана prepare-to-call через RequestParticipationButton,
      // а не из диалога вступления в чат.
      return true
    }

    return !!(await showDialog(JoinToChatAwaitDialog, dialogProps))
  }

  return !!(await showDialog(JoinToChatDialog, dialogProps))
}
